import { MODAL_TYPE } from 'config/Constants';
import { Job } from 'Interfaces/JobsInterface';
import { DataTableRowReorderEvent } from 'primereact/datatable';
import { toast } from 'react-toastify';
import { yjsCollaborationService } from 'services/yjsCollaborationService';
import { canUpdateJobOrder } from 'utils/AuthUtils/ppsAccessControl';
import { useScrollPreservation } from '../../../../hooks/useScrollPreservation';
import { useStore } from '../../../../hooks/useStore';

export interface Option {
  label: string;
  value: string;
}

export interface NumberOption {
  label: string;
  value: number | string;
}

export type Options = Option[];
export type NumberOptions = NumberOption[];

/**
 * Helper function to create job serials map from reordered jobs
 * @param reorderedJobs - Array of jobs after reordering
 * @returns Object mapping jobId to serial number
 */
const createJobSerialsMap = (reorderedJobs: Job[]): Record<string, number> => {
  const jobSerials: Record<string, number> = {};
  reorderedJobs.forEach(job => {
    if (job.jobId && job.serial !== undefined) {
      jobSerials[job.jobId] = job.serial;
    }
  });
  return jobSerials;
};

export const useRoomAccordion = () => {
  const {
    JobPasteStore,
    GlobalModalStore,
    roomStore,
    runTypeStore,
    statusCodesStore,
    cleanTypeStore,
    UserStore
  } = useStore();

  // Use the scroll preservation hook
  const { executeWithScrollPreservation } = useScrollPreservation();

  const roomOptionLoading = roomStore.isLoading;
  const jobTypesOptionLoading = runTypeStore.isLoading;
  const statusOptionsLoading =
    statusCodesStore.possibleTransitionsLoading || statusCodesStore.isLoading;
  const cleanTypeIsLoading = cleanTypeStore.isLoading;

  const department = UserStore.userDepartment;

  // Prepare room options from roomStore
  const roomOptions = roomStore.rooms.map(room => ({
    label: `${room.room_long}`,
    value: `${room.room_long.toLowerCase()}`
  }));

  const typeOptions = runTypeStore.runTypes.map(jobType => ({
    label: `${jobType.description}`,
    value: `${jobType.description.toLowerCase()}`
  }));

  const getStatusOptionsForJob = (currentStatus: string): Options => {
    // If no current status, return empty array
    if (!currentStatus) {
      return [];
    }

    // If status transitions aren't loaded yet, return all status codes
    if (
      !statusCodesStore.possibleTransitions ||
      Object.keys(statusCodesStore.possibleTransitions).length === 0
    ) {
      return statusCodesStore.statusCodes.map(statusCode => ({
        label: statusCode.description || '',
        value: statusCode.description?.toLowerCase() || ''
      }));
    }

    // Normalize the status to lowercase for lookup
    const normalizedStatus = currentStatus.toLowerCase();

    // Create current status option
    const currentStatusOption = {
      label: currentStatus,
      value: normalizedStatus
    };

    // Get transitions for the current status
    const transitions = statusCodesStore.possibleTransitions[normalizedStatus] || [];

    // If no transitions, return current status as the only option
    if (transitions.length === 0) {
      return [currentStatusOption];
    }

    // Map transitions to options format
    const transitionOptions = transitions.map(transition => ({
      label: transition.description,
      value: transition.description.toLowerCase()
    }));

    // Check if current status is included in the transitions
    const currentStatusIncluded = transitionOptions.some(
      option => option.value === normalizedStatus
    );

    // If not included, add it to the beginning of the array
    if (!currentStatusIncluded) {
      return [currentStatusOption, ...transitionOptions];
    }

    return transitionOptions;
  };

  // Default status options - used as a fallback
  const statusOptions = statusCodesStore.statusCodes.map(statusCode => ({
    label: `${statusCode.description}`,
    value: `${statusCode.description?.toLowerCase()}`
  }));

  const clearnOverrideOptions = cleanTypeStore.cleanTypes.map(cleanType => ({
    label: `${cleanType.description.toUpperCase()}`,
    value: `${cleanType.description?.toUpperCase()}`
  }));

  const priotityOptions: NumberOptions = [
    { label: '1', value: '1' },
    { label: '3', value: '3' }
  ];

  // Handler for updating edited values
  const onFieldChange = (
    jobId: string,
    roomId: string,
    field: string,
    value: string | number | Date
  ) => {
    // Find the room and job in the cloned data and update it
    if (JobPasteStore._cloned_jobs_for_edits === null) {
      return toast.error('Room not in edit mode');
    }

    const roomIndex = JobPasteStore._cloned_jobs_for_edits.findIndex(room => room.id === roomId);

    if (roomIndex !== -1) {
      const jobIndex = JobPasteStore._cloned_jobs_for_edits[roomIndex].jobs.findIndex(
        job => job.jobId === jobId
      );

      if (jobIndex !== -1) {
        // Just set the value directly without any casing modifications
        (JobPasteStore._cloned_jobs_for_edits[roomIndex].jobs[jobIndex] as any)[field] = value;

        // Track the field change with timestamp
        JobPasteStore.trackFieldChange(jobId, roomId, field);
        // Trigger re-render for fields that affect UI state or field editing indicators
        if (
          field === 'status' ||
          field === 'priority' ||
          field === 'room'
          // field === 'jobStartTime'
        ) {
          // These fields need immediate re-render to update UI state and field editing indicators
          JobPasteStore._cloned_jobs_for_edits = [...JobPasteStore._cloned_jobs_for_edits];
        }
        // For other fields, don't trigger re-render on every keystroke
        // The value is still updated in the store, just not causing UI lag
      }
    }
  };

  const isRBACAllowed = canUpdateJobOrder(department, UserStore.ppsUserRole);

  // Improved row reorder handler that immediately updates the UI
  const onRowReorder = (e: DataTableRowReorderEvent<Job[]>, roomId: string) => {
    if (!JobPasteStore._cloned_jobs_for_edits) {
      toast.error('Room not in edit mode');
      return;
    }

    const roomIndex = JobPasteStore._cloned_jobs_for_edits.findIndex(room => room.id === roomId);

    if (roomIndex === -1) {
      toast.error(`Room with ID ${roomId} not found`);
      return;
    }

    // Get the reordered jobs array from the event
    const reorderedJobs = [...e.value] as Job[];

    // Get the original jobs array to compare positions
    const originalJobs = JobPasteStore._cloned_jobs_for_edits[roomIndex].jobs;

    // Create a map of original positions for comparison
    const originalPositions = new Map<string, number>();
    originalJobs.forEach((job, index) => {
      originalPositions.set(job.jobId, index);
    });

    // Step 1: Assign new serial numbers with a gap (step size) to allow for future insertions
    const STEP_SIZE = 10;

    // Prepare bulk field changes for tracking
    const fieldChanges: Array<{ jobId: string; field: string }> = [];
    const movedJobs: Array<{ jobId: string; fromPosition: number; toPosition: number }> = [];

    reorderedJobs.forEach((job, index) => {
      const originalPosition = originalPositions.get(job.jobId);
      const newPosition = index;

      // Update the serial number for each job in the reordered array
      job.serial = (index + 1) * STEP_SIZE;

      // Track serial field change for all jobs (since all serials are updated)
      fieldChanges.push({ jobId: job.jobId, field: 'serial' });

      // If the job position changed, track it as a position change (but don't duplicate serial)
      if (originalPosition !== undefined && originalPosition !== newPosition) {
        // Note: We don't need to add 'serial' again since it's already added above
        // The position change is tracked by the serial field change
        movedJobs.push({
          jobId: job.jobId,
          fromPosition: originalPosition,
          toPosition: newPosition
        });
      }
    });

    // Step 2: Use bulk tracking for all field changes
    JobPasteStore.trackBulkFieldChanges(roomId, fieldChanges);

    // Step 3: Replace the jobs array with the reordered one to immediately reflect changes in UI
    JobPasteStore._cloned_jobs_for_edits[roomIndex].jobs = reorderedJobs;

    // Step 4: Send WebSocket message for real-time collaboration (using service directly)
    const room = JobPasteStore._cloned_jobs_for_edits[roomIndex];
    if (yjsCollaborationService.isWebSocketConnected()) {
      const jobSerials = createJobSerialsMap(reorderedJobs);
      yjsCollaborationService.sendJobReorder(room.name, jobSerials);
    } else {
      console.warn('⚠️ WebSocket not connected, job reorder will not be broadcast to other users');
    }

    // Step 5: Log all affected jobs for debugging
    const affectedJobs = reorderedJobs.map(job => ({
      jobId: job.jobId,
      newSerial: job.serial,
      newPosition: reorderedJobs.indexOf(job),
      originalPosition: originalPositions.get(job.jobId),
      moved: movedJobs.some(moved => moved.jobId === job.jobId)
    }));

    // Provide user feedback
    const movedCount = movedJobs.length;
    const totalCount = affectedJobs.length;
    const message =
      movedCount > 0
        ? `Job order updated for ${totalCount} jobs (${movedCount} positions changed). Save changes to apply to the server.`
        : `Job order updated for ${totalCount} jobs. Save changes to apply to the server.`;

    toast.info(message);
  };

  const { isEditMode, disableEditMode, enableEditMode } = JobPasteStore;

  const onEditClick = async (roomId: string) => {
    try {
      await executeWithScrollPreservation(async () => {
        await enableEditMode(roomId);
      });

      // Notify WebSocket that user is starting to edit this room
      // This will be handled by the parent component
    } catch {
      toast.error('Failed to enter edit mode. Please try again.');
    }
  };

  const onCancelClick = () => {
    executeWithScrollPreservation(() => {
      disableEditMode();
    });

    // Notify WebSocket that user is stopping editing
    // This will be handled by the parent component
  };

  const onEditedRoomSave = (roomId: string) => {
    executeWithScrollPreservation(() => {
      const didOverrideHappen = JobPasteStore.onSaveOfEditedJobsInARoom(roomId);
      if (didOverrideHappen !== undefined) {
        GlobalModalStore.toggleModal(MODAL_TYPE.PPS_JOB_PASTE_OVERRIDE_CAPTURE_MODAL);
      } else {
        JobPasteStore.saveEditedRoomJobs(roomId);
      }
    });
  };

  return {
    JobPasteStore,
    onEditClick,
    onCancelClick,
    isEditMode,
    getStatusOptionsForJob,
    statusOptions,
    typeOptions,
    roomOptions,
    onFieldChange,
    priotityOptions,
    clearnOverrideOptions,
    onEditedRoomSave,
    roomOptionLoading,
    jobTypesOptionLoading,
    statusOptionsLoading,
    cleanTypeIsLoading,
    onRowReorder,
    department,
    isRBACAllowed
  };
};
